import React, { useMemo, useState } from 'react';
import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import theme from 'theme';
import { ModalItemCompanyBoxProps } from './ModelItemCompany.props';
import { SCALAR_IO } from '../../constants';
import CompanyUserIconAndRedirectionButton from '../CompanyUserIconAndRedirectionButton';

const ModalItemCompanyBox = ({
  company,
  includeCheckbox,
  backgroundColor,
  padding,
  handleSelectCompanies,
  selectedCompanies,
  displayCompanyUser = true,
}: ModalItemCompanyBoxProps) => {
  const { name: companyName, users_with_company_permissions: usersWithCompanyPermissions } = company;
  const isNonProductionEnv = process.env.REACT_APP_ENVIRONMENT !== 'production';
  const [onlyStaffEmails, setOnlyStaffEmails] = useState(isNonProductionEnv);
  const listOfUserNames = useMemo(() => {
    const emails = usersWithCompanyPermissions?.map(user => user.email) || [];
    if (onlyStaffEmails) {
      return emails.filter(email => email.endsWith(SCALAR_IO)).join(', ');
    }
    return emails.join(', ');
  }, [usersWithCompanyPermissions, onlyStaffEmails]);

  const companyIsSelected = useMemo(
    () => selectedCompanies?.some(selectedCompany => selectedCompany.id === company.id),
    [selectedCompanies, company]
  );

  const handleChange = (status: { selected: boolean; onlyStaffEmails: boolean }) => {
    setOnlyStaffEmails(status.onlyStaffEmails);
    handleSelectCompanies?.(company, status);
  };

  return (
    <Box
      border={`1px solid ${theme.palette.grey[300]}`}
      borderRadius={4}
      marginBottom={0.5}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      padding={padding ?? 0}
      style={{
        backgroundColor: backgroundColor ?? 'transparent',
      }}>
      <Box display="flex" alignItems="center">
        {includeCheckbox && (
          <Checkbox
            onChange={() => handleChange({ selected: !companyIsSelected, onlyStaffEmails })}
            checked={companyIsSelected}
          />
        )}
        <Typography
          style={{
            color: theme.palette.grey[500],
            fontSize: '0.875rem',
            fontWeight: 700,
          }}>
          {companyName}
        </Typography>
      </Box>
      {includeCheckbox && displayCompanyUser && (
        <Box display="flex" alignItems="center">
          {isNonProductionEnv && companyIsSelected && (
            <Tooltip
              title={
                <div style={{ whiteSpace: 'pre-line' }}>
                  {onlyStaffEmails
                    ? 'Checked: Only Scalar users will receive emails.\n(This checkbox is visible only in non-production environments)'
                    : 'Unchecked: All company users will receive emails.\n(This checkbox is visible only in non-production environments)'}
                </div>
              }
              placement="top">
              <Checkbox
                onChange={() => handleChange({ selected: companyIsSelected, onlyStaffEmails: !onlyStaffEmails })}
                checked={onlyStaffEmails}
                style={{ color: 'red' }}
              />
            </Tooltip>
          )}
          <CompanyUserIconAndRedirectionButton usersEmails={listOfUserNames.split(',')} />
        </Box>
      )}
    </Box>
  );
};

export default ModalItemCompanyBox;
